import dayjs from 'dayjs';
import TimeUtils from '@spothero/utils/time';

/**
 * This file is used as a util for dayjs to allow for easier migration if we change time libraries again.
 * Also all utils that need to be written for dayjs will exist here until the fe-utils migration.
 *
 * @module dayjs
 */

/**
 * Object instance of Dayjs.  Containing all relevant methods and keys
 *
 * @typedef Dayjs
 * @type {object}
 */

/**
 * Splits apart dates delimited by argument
 *
 * @static
 * @function splitMobileDate
 * @param {string} date - a string with with the pattern MM/DD/YYYY
 * @param {string} delimiter - a string with with the delimiter of the date (example above is of / as delimiter)
 * @example
 * DateUtils.splitMobileDate('1/30/1991', '/')
 * @returns {Date} - Date object with year, month, and day set
 */
const splitTextDate = (date, delimiter) => {
    const d = date.split(delimiter);

    return new Date(d[2], d[0] - 1, d[1]);
};

/**
 * Splits apart dates delimited by hyphens
 *
 * @static
 * @function splitMobileDate
 * @param {string} date - a string with with the pattern YYYY-MM-DD, YYYY-M-D, ...etc
 * @example
 * DateUtils.splitMobileDate('1887-1-16')
 * @returns {Date} - Date object with year, month, and day set
 */
const splitMobileDate = date => {
    const d = date.split('-');

    return new Date(d[0], d[1] - 1, d[2]);
};

/**
 * Combines a date dayjs with a time dayjs to return one dayjs instance. (based on OG implementation)
 *
 * @static
 * @function combineDateAndTime
 * @param {Dayjs} date - The date as a dayjs instance.
 * @param {Dayjs} time - The time as a dayjs instance.
 * @example
 * DateUtils.combineDateAndTime(dayjs(), dayjs().add(7, 'hours').add(23, 'minutes'));
 * @returns {Dayjs} - The combined dayjs instance.
 */

export const combineDateAndTime = (date, time) => {
    if (!dayjs.isDayjs(date) || !dayjs.isDayjs(time)) {
        throw new Error(
            'The date and/or time supplied was not an instance of Dayjs.'
        );
    }

    return date.hour(time.hour()).minute(time.minute());
};

/**
 * Converts a string date from a text or date input to a dayjs instance.
 *
 * @static
 * @function getAsDayjsDate
 * @param {string} date - The date in the format MM/DD/YYYY (text input) or YYYY-MM-DD (date input).
 * @example
 * DateUtils.getAsDayjs('1983-01-19');
 * DateUtils.getAsDayjs('01/19/1983');
 * @returns {Dayjs} - The dayjs instance.
 */

export const getAsDayjsDate = date => {
    if (typeof date !== 'string') {
        throw new Error('The supplied date is not a string.');
    }

    if (
        // Not MM/DD/YYYY and not YYYY-MM-DD.  Includes considerations for single digit months and days.
        !date.match(/^\d{4}\-(0?[1-9]|1[012])\-(0?[1-9]|[12][0-9]|3[01])$/) &&
        !date.match(
            /(0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])[- \/.](19|20)\d\d/
        )
    ) {
        throw new Error('The supplied date not formatted as expected.');
    }

    if (date.indexOf('-') !== -1) {
        // date input
        return dayjs(splitMobileDate(date));
    } else {
        // text input
        return dayjs(splitTextDate(date, '/'));
    }
};

/**
 * Converts a time to a dayjs instance.
 *
 * @static
 * @function getAsDayjsTime
 * @param {string} time - The input value in the format hh:mm A.
 * @example
 * TimeUtils.getAsDayjs('10:19 PM');
 * @returns {Dayjs} - The time inside of a dayjs instance.
 */

export const getAsDayjsTime = time => {
    if (!time) {
        return null;
    }

    if (typeof time !== 'string') {
        throw new Error('The supplied time is not a string.');
    }

    const {hours, mins} = TimeUtils.getValuesFromString(time);

    return dayjs().startOf('day').add(hours, 'hours').add(mins, 'minutes');
};

export default dayjs;
